import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import SearchableSelect from '../components/SearchableSelect';
import { useConfig } from '../context/ConfigContext';

// TS port of the source (Falah) app's pages/CustomerQuotationForm.jsx. Only
// handles create — editing an existing quotation happens inline on
// CustomerQuotationDetail.tsx (source-app convention, kept here). Field set
// matches CustomerQuotationController::validated() exactly — no `type`/
// `category` here (those exist on CustomerInvoice/Receipt, not on
// CustomerQuotation).
interface QuotationFormState {
  customer_id: string | number;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  service: string;
  quotation_date: string;
  amount: string;
  valid_until: string;
  notes: string;
  raise_invoice: boolean;
}

const emptyForm: QuotationFormState = {
  customer_id: '',
  customer_name: '',
  customer_phone: '',
  customer_email: '',
  service: '',
  quotation_date: new Date().toISOString().slice(0, 10),
  amount: '',
  valid_until: '',
  notes: '',
  raise_invoice: false,
};

interface CustomerOption {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  [key: string]: unknown;
}

type FormErrors = Record<string, string[]>;

export function CustomerQuotationForm() {
  const { settings } = useConfig();
  const navigate = useNavigate();
  const [form, setForm] = useState<QuotationFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  const set =
    (key: keyof QuotationFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const setRaiseInvoice = (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, raise_invoice: e.target.checked });

  const selectCustomer = (id: string | number, customer?: CustomerOption) => {
    setForm({
      ...form,
      customer_id: id,
      customer_name: customer?.name ?? form.customer_name,
      customer_phone: customer?.phone ?? form.customer_phone,
      customer_email: customer?.email ?? form.customer_email,
    });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const res = await api.post<{ id: number; invoice: { id: number } | null }>('/customer-quotations', form);
      navigate(res.data.invoice ? `/revenue/invoices/${res.data.invoice.id}` : `/revenue/quotations/${res.data.id}`);
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Add quotation</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
          <label className="span-2">
            Customer
            <SearchableSelect<CustomerOption>
              endpoint="/customers"
              value={form.customer_id}
              onChange={(id, customer) => selectCustomer(id, customer)}
              getLabel={(c) => c.name}
              getSubLabel={(c) => c.email || c.phone || ''}
              placeholder="Search customers…"
            />
            <small className="muted">Selecting a customer fills in the name, phone, and email below.</small>
          </label>
          <label className="span-2">
            Customer name *
            <input value={form.customer_name} onChange={set('customer_name')} required />
            {errors.customer_name && <small className="error">{errors.customer_name[0]}</small>}
          </label>
          <label>
            Customer phone
            <input type="tel" value={form.customer_phone} onChange={set('customer_phone')} />
            {errors.customer_phone && <small className="error">{errors.customer_phone[0]}</small>}
          </label>
          <label>
            Customer email
            <input type="email" value={form.customer_email} onChange={set('customer_email')} />
            {errors.customer_email && <small className="error">{errors.customer_email[0]}</small>}
          </label>
          <label className="span-2">
            Service *
            <input value={form.service} onChange={set('service')} placeholder="e.g. Hall Rental" required />
            {errors.service && <small className="error">{errors.service[0]}</small>}
          </label>
          <label>
            Amount ({settings.currency}) *
            <input type="number" step="0.01" min="0.01" value={form.amount} onChange={set('amount')} required />
            {errors.amount && <small className="error">{errors.amount[0]}</small>}
          </label>
          <label>
            Quotation date *
            <input type="date" value={form.quotation_date} onChange={set('quotation_date')} required />
          </label>
          <label>
            Valid until
            <input type="date" value={form.valid_until} onChange={set('valid_until')} />
          </label>
          <label className="span-2">
            Notes
            <textarea rows={2} value={form.notes} onChange={set('notes')} />
          </label>
          <label className="checkbox-inline span-2">
            <input type="checkbox" checked={form.raise_invoice} onChange={setRaiseInvoice} />
            Raise an invoice for this straight away
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/revenue/quotations">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
